/**
 * Markdown Sanitizer for Guest Book
 * 
 * Supports safe markdown rendering with:
 * - Bold, Italic, Lists, Quotes, Headings
 * - Links rendered as plain text for transparency
 * - No images, HTML, or scripts
 * - Character limit enforcement
 */

import { marked } from 'marked'
import sanitizeHtml from 'sanitize-html'

const MAX_MESSAGE_LENGTH = 1000 // characters

export interface SanitizedMarkdown {
  html: string         // Sanitized HTML output
  raw: string          // Original markdown
  linkCount: number    // Number of links found (for spam detection)
  characterCount: number
  truncated: boolean   // If content was truncated
}

/**
 * Sanitize and render markdown for guest book messages
 * Links are converted to plain text to prevent spam
 */
export function sanitizeGuestBookMarkdown(markdown: string): SanitizedMarkdown {
  if (!markdown || markdown.trim().length === 0) {
    return {
      html: '',
      raw: '',
      linkCount: 0,
      characterCount: 0,
      truncated: false
    }
  }

  // Enforce character limit
  let truncated = false
  let processedMarkdown = markdown.trim()
  
  if (processedMarkdown.length > MAX_MESSAGE_LENGTH) {
    processedMarkdown = processedMarkdown.substring(0, MAX_MESSAGE_LENGTH)
    truncated = true
  }

  // Count links before conversion
  const linkCount = countLinks(processedMarkdown)

  // Convert links to plain text BEFORE markdown rendering
  const markdownWithPlainLinks = convertLinksToPlainText(processedMarkdown)

  // Configure marked options
  marked.setOptions({
    breaks: true, // Convert line breaks to <br>
    gfm: true,    // GitHub Flavored Markdown
    pedantic: false,
    silent: false
  })

  // Render markdown to HTML
  let html = marked.parse(markdownWithPlainLinks) as string

  // Sanitize HTML to remove any dangerous content
  html = sanitizeHTML(html)

  return {
    html,
    raw: markdown,
    linkCount,
    characterCount: processedMarkdown.length,
    truncated
  }
}

/**
 * Count links in markdown
 * Matches both [text](url) format and raw URLs
 */
function countLinks(markdown: string): number {
  // Match markdown links [text](url)
  const markdownLinks = markdown.match(/\[([^\]]+)\]\(([^)]+)\)/g) || []
  
  // Match raw URLs
  const rawUrls = markdown.match(/https?:\/\/[^\s]+|www\.[^\s]+/gi) || []
  
  return markdownLinks.length + rawUrls.length
}

/**
 * Convert all links to plain text format
 * [text](url) becomes "text (url)"
 * Raw URLs stay as plain text
 */
function convertLinksToPlainText(markdown: string): string {
  let converted = markdown

  // Convert markdown-style links [text](url) to "text (url)"
  converted = converted.replace(
    /\[([^\]]+)\]\(([^)]+)\)/g,
    (match, text, url) => {
      // Show both text and URL for transparency
      return `${text} (${url})`
    }
  )

  // Raw URLs are left as-is (they'll appear as plain text)
  // Markdown won't auto-link them after we process through DOMPurify

  return converted
}

/**
 * Sanitize HTML output
 * Only allows safe tags and attributes
 */
function sanitizeHTML(html: string): string {
  return sanitizeHtml(html, {
    allowedTags: ['p', 'br', 'strong', 'em', 'ul', 'ol', 'li', 'blockquote', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'code', 'pre'],
    allowedAttributes: {},
    disallowedTagsMode: 'discard',
  })
}

/**
 * Strip all markdown and return plain text only
 * Useful for preview or length calculation
 */
export function stripMarkdown(markdown: string): string {
  let text = markdown

  // Remove markdown links
  text = text.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')

  // Remove bold/italic
  text = text.replace(/(\*\*|__)(.*?)\1/g, '$2')
  text = text.replace(/(\*|_)(.*?)\1/g, '$2')

  // Remove headings
  text = text.replace(/^#{1,6}\s+/gm, '')

  // Remove blockquotes
  text = text.replace(/^>\s+/gm, '')

  // Remove list markers
  text = text.replace(/^[\*\-\+]\s+/gm, '')
  text = text.replace(/^\d+\.\s+/gm, '')

  // Remove code blocks
  text = text.replace(/```[^`]*```/g, '')
  text = text.replace(/`([^`]+)`/g, '$1')

  return text.trim()
}

/**
 * Get a preview excerpt from markdown
 * Returns first N characters as plain text
 */
export function getMarkdownPreview(markdown: string, maxLength: number = 200): string {
  const plainText = stripMarkdown(markdown)
  
  if (plainText.length <= maxLength) {
    return plainText
  }

  // Find last complete word within limit
  const truncated = plainText.substring(0, maxLength)
  const lastSpace = truncated.lastIndexOf(' ')
  
  if (lastSpace > 0) {
    return truncated.substring(0, lastSpace) + '...'
  }

  return truncated + '...'
}

/**
 * Validate markdown content
 * Returns error message if invalid, null if ok
 */
export function validateMarkdown(markdown: string): string | null {
  if (!markdown || markdown.trim().length === 0) {
    return 'Message cannot be empty'
  }

  if (markdown.length > MAX_MESSAGE_LENGTH) {
    return `Message exceeds maximum length of ${MAX_MESSAGE_LENGTH} characters`
  }

  // Check for excessive link spam
  const linkCount = countLinks(markdown)
  if (linkCount > 3) {
    return 'Too many links in message (maximum 3)'
  }

  return null
}

/**
 * Render markdown for display with specific styling context
 * Applies additional classes for different contexts
 */
export function renderMarkdownForDisplay(
  markdown: string,
  context: 'guest-book' | 'preview' | 'admin'
): SanitizedMarkdown {
  const sanitized = sanitizeGuestBookMarkdown(markdown)

  // Add context-specific wrapper classes if needed
  if (context === 'preview') {
    sanitized.html = `<div class="markdown-preview">${sanitized.html}</div>`
  } else if (context === 'admin') {
    sanitized.html = `<div class="markdown-admin">${sanitized.html}</div>`
  } else {
    sanitized.html = `<div class="markdown-guest-book">${sanitized.html}</div>`
  }

  return sanitized
}

/**
 * Check if markdown contains suspicious patterns
 * Returns true if content looks spammy
 */
export function hasSuspiciousMarkdown(markdown: string): boolean {
  // Too many links
  if (countLinks(markdown) > 3) {
    return true
  }

  // Excessive use of formatting (potential spam)
  const boldCount = (markdown.match(/\*\*|\__/g) || []).length / 2
  const italicCount = (markdown.match(/\*|_/g) || []).length / 2
  const totalFormatting = boldCount + italicCount

  if (totalFormatting > 10) {
    return true
  }

  // Excessive headings (spam pattern)
  const headingCount = (markdown.match(/^#{1,6}\s/gm) || []).length
  if (headingCount > 3) {
    return true
  }

  return false
}

/**
 * Get markdown statistics
 * Useful for display and validation
 */
export interface MarkdownStats {
  characterCount: number
  wordCount: number
  lineCount: number
  linkCount: number
  formattingCount: number
  headingCount: number
}

export function getMarkdownStats(markdown: string): MarkdownStats {
  const lines = markdown.split('\n')
  const words = markdown.trim().split(/\s+/).filter(w => w.length > 0)
  
  return {
    characterCount: markdown.length,
    wordCount: words.length,
    lineCount: lines.length,
    linkCount: countLinks(markdown),
    formattingCount: (markdown.match(/\*\*|__|\*|_/g) || []).length,
    headingCount: (markdown.match(/^#{1,6}\s/gm) || []).length
  }
}

